const MAIN_DOMAIN = 'skhool.in';
const ROOT_HOSTS = new Set([MAIN_DOMAIN, `www.${MAIN_DOMAIN}`]);
const LOCAL_QUERY_KEYS = ['tenant', 'subdomain', 'school'];
const LOCAL_STORAGE_KEYS = ['tenantKey', 'tenant', 'previewTenant'];

const sanitizeTenant = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const withoutProtocol = normalized.replace(/^https?:\/\//, '');
  const hostnameOnly = withoutProtocol.split('/')[0];
  const safeValue = hostnameOnly.replace(/[^a-z0-9.-]/g, '');

  return safeValue || null;
};

const getBrowserHostname = () => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return '';
  }

  return window.location.hostname.trim().toLowerCase();
};

const isLocalHost = (hostname) =>
  Boolean(hostname) &&
  (hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname.endsWith('.local'));

const getLocalPreviewTenant = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  for (const key of LOCAL_QUERY_KEYS) {
    const value = sanitizeTenant(params.get(key));
    if (value && value !== 'demo') {
      return value;
    }
  }

  for (const key of LOCAL_STORAGE_KEYS) {
    const value = sanitizeTenant(window.localStorage.getItem(key));
    if (value && value !== 'demo') {
      return value;
    }
  }

  return null;
};

export const resolveTenantContext = () => {
  const hostname = getBrowserHostname();

  if (!hostname) {
    return {
      hostname: '',
      tenantKey: null,
      isRootDomain: true,
      isCustomDomain: false,
      source: 'unknown',
    };
  }

  if (isLocalHost(hostname)) {
    const localTenant = getLocalPreviewTenant();
    return {
      hostname,
      tenantKey: localTenant,
      isRootDomain: !localTenant,
      isCustomDomain: false,
      source: localTenant ? 'local-preview' : 'local-root',
    };
  }

  if (ROOT_HOSTS.has(hostname)) {
    return {
      hostname,
      tenantKey: null,
      isRootDomain: true,
      isCustomDomain: false,
      source: 'root-domain',
    };
  }

  if (hostname.endsWith(`.${MAIN_DOMAIN}`)) {
    const labels = hostname.split('.');
    const subdomain = sanitizeTenant(labels.slice(0, -2).join('.'));

    if (!subdomain || subdomain === 'www') {
      return {
        hostname,
        tenantKey: null,
        isRootDomain: true,
        isCustomDomain: false,
        source: 'invalid-subdomain',
      };
    }

    return {
      hostname,
      tenantKey: subdomain,
      isRootDomain: false,
      isCustomDomain: false,
      source: 'subdomain',
    };
  }

  return {
    hostname,
    tenantKey: hostname,
    isRootDomain: false,
    isCustomDomain: true,
    source: 'custom-domain',
  };
};

export const getTenantKey = () => resolveTenantContext().tenantKey;
export const isRootDomainHost = () => resolveTenantContext().isRootDomain;

// Backward-compatible alias used across the existing codebase.
export const getRootSubdomain = () => {
  const context = resolveTenantContext();
  if (context.tenantKey) {
    return context.tenantKey;
  }
  return null;
};
